import { useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

// Hooks
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { BASE } from '@/types/utils/sizesType';
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

// Types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';
import type { UserNumberFormatType } from '@/types/userType';

import { INSTAGRAM, PINTEREST, YOUTUBE } from '@/lib/socialProfile';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
// Lib
import { iconSize } from '@/components/base/Avatar/avatarConfig';
import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import Instagram from '@/components/icons/Instagram';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';
import ErrorAnalyticsFallback from '@/components/layouts/ErrorFallbacks/ErrorAnalyticsFallback';

// Render the appropriate icon based on the network type
const SocialNetworkIcon = ({ network }: { network: SocialProfileNetworkType }) => {
    const { isDarkMode } = useDarkMode();

    switch (network) {
        case INSTAGRAM:
            return !isDarkMode() ? (
                <Instagram />
            ) : (
                <Icon
                    icon={network}
                    className={`${iconSize[BASE]} text-facebook dark:text-white`}
                />
            );
        case PINTEREST:
            return <PinterestIcon />;
        case YOUTUBE:
            return <YoutubeIcon />;
        default:
            return (
                <Icon
                    icon={network}
                    className={`${iconSize[BASE]} text-facebook dark:text-white`}
                />
            );
    }
};

type ListProps = {
    network: SocialProfileNetworkType;
    onReset: () => void;
    isLoading: boolean;
    config: {
        title: string;
        path: string;
        valueFormat?: Intl.NumberFormatOptions;
    };
    numberFormat: UserNumberFormatType;
    data?: ItemType[];
};

export const List = ({ network, onReset, isLoading, config, numberFormat, data }: ListProps) => {
    const { t } = useTranslation();
    const { formatNumber } = useFormatNumber(numberFormat);
    const { activeSocialProfile } = useActiveSocialProfile();

    const finalData = useMemo(
        () =>
            data.sort((a, b) => {
                return b.value - a.value;
            }),
        [data]
    );

    // List is loading
    if (isLoading) {
        return (
            <div
                className="h-full animate-pulse space-y-6 rounded-md bg-white py-8 dark:bg-icoDarkMode-anthracite"
                data-testid="list-loading">
                <div className="flex items-center space-x-3 px-8">
                    <div className="h-7 w-64 bg-gray-100 dark:bg-icoDarkMode-deepBlack"></div>
                </div>
                <div className="p-1">
                    <div className={'flex items-center space-x-3 px-8 py-3'}>
                        <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-icoDarkMode-deepBlack"></div>
                        <span className="h-7 w-4/6 bg-gray-100 dark:bg-icoDarkMode-deepBlack"></span>
                    </div>

                    <div className={'flex items-center space-x-3 px-8 py-3'}>
                        <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-icoDarkMode-deepBlack"></div>
                        <span className="h-7 w-4/6 bg-gray-100 dark:bg-icoDarkMode-deepBlack"></span>
                    </div>

                    <div className={'flex items-center space-x-3 px-8 py-3'}>
                        <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-icoDarkMode-deepBlack"></div>
                        <span className="h-7 w-4/6 bg-gray-100 dark:bg-icoDarkMode-deepBlack"></span>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <ErrorBoundary FallbackComponent={ErrorAnalyticsFallback} onReset={() => onReset()}>
            <div
                className="flex h-full w-full flex-col space-y-6 rounded-md bg-white py-8 transition duration-200 hover:shadow-icoXL dark:bg-icoDarkMode-anthracite dark:hover:shadow-icoDarkXL"
                data-testid="list-wrapper">
                <div className="flex items-center space-x-3 px-8">
                    <SocialNetworkIcon network={network} />
                    <span className="font-GSemiBold">{t(config.title)}</span>
                </div>
                {finalData.length > 0 ? (
                    <div className="scrollbar max-h-[26rem] divide-y divide-icoGray-300 overflow-y-auto px-8 dark:divide-icoDarkMode-goudron">
                        {finalData.map((element) => {
                            if (element.id !== activeSocialProfile.id) {
                                return (
                                    <LinkBase
                                        key={element.id}
                                        href={`${config.path}/${element.id}`}
                                        className={'group flex items-center py-3'}
                                        disableFocusRing
                                        data-testid="list-item">
                                        <List.Item
                                            element={element}
                                            formatNumber={formatNumber}
                                            valueFormat={config?.valueFormat}
                                        />
                                    </LinkBase>
                                );
                            }
                            return (
                                <div
                                    key={element.id}
                                    className={
                                        '-mx-8 flex items-center bg-icoBlue-100 px-8 py-3 dark:bg-icoBlue/20'
                                    }
                                    data-testid="list-item-active">
                                    <List.Item
                                        element={element}
                                        formatNumber={formatNumber}
                                        valueFormat={config?.valueFormat}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div
                        className="flex h-full min-h-[274px] w-full items-center justify-center px-8"
                        data-testid="list-no-data">
                        <div className="flex flex-col items-center">
                            <div className="flex h-15 w-15 items-center justify-center rounded-full bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                                <SocialNetworkIcon network={network} />
                            </div>
                            <div data-testid="no-data" className="mt-5 text-xs text-icoGray-500">
                                {t('analytics:no_data_period')}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};

type ItemType = {
    name: string;
    picture: string;
    username: string;
    value: number;
    id: number;
};

type ItemProps = {
    element: ItemType;
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
    valueFormat: Intl.NumberFormatOptions;
};

const Item = ({ element, formatNumber, valueFormat }: ItemProps) => {
    const { name, picture, username, value } = element;

    return (
        <>
            <span>
                <Avatar url={picture} fallback={username?.charAt(0)?.toUpperCase()} />
            </span>
            <div className="ml-4 flex w-full items-center justify-between space-x-3">
                <div className="flex flex-col space-y-1">
                    <span className="font-GSemiBold text-sm group-hover:text-icoBlue">
                        {username}
                    </span>
                    <span className="font-GMedium text-xs text-icoGray-500">{name}</span>
                </div>
                <div>
                    {valueFormat ? (
                        <span className="font-GSemiBold text-sm" data-testid="item-value-format">
                            {formatNumber(value, valueFormat)}
                        </span>
                    ) : null}
                </div>
            </div>
        </>
    );
};

List.Item = Item;
